import { createHashRouter, Navigate } from "react-router-dom";
import { lazy } from "react";
import { Config } from "./constants/config";

import { AllReconciliations } from "./components/allReconciliations/allReconciliations";
import { RuleSummaryService2 } from "./services/rule.service";
import { TemplateService2 } from "./services/template.service";
import { ReconciliationDetailsService } from "./services/reconciliationDetails.service";
import { ReconciliationHome } from "./ReconciliationHome";
import { IReconciliationStatus } from "./components/reconciliationDetails/reconciliationDetailsPage.interface";
import { dateFormat } from "./components/utils/commonFunctions";
import { Settings } from "./components/settings/settings";
import { SettingsService2 as settingsService } from "./services/settings.service";
import LocationTracker from "./LocationTracker";

const ReconciliationForm = lazy(() => import("./components/simpleFormPage/reconciliationForm").then(module => ({ default: module.ReconciliationForm })));
const AllTemplates = lazy(() => import('./components/template/allTemplates').then(module => ({ default: module.AllTemplates })));
const TemplateForm = lazy(() => import('./components/template/templateForm').then(module => ({ default: module.TemplateForm })));
const AllRules = lazy(() => import('./components/reconciliationRules/allRules').then(module => ({ default: module.AllRules })));
const RulesetForm = lazy(() => import('./components/reconciliationRules/ruleSetForm').then(module => ({ default: module.RulesetForm })));
const ReconciliationDetailsPage = lazy(() => import("./components/reconciliationDetails/reconciliationDetailsPage").then(module => ({ default: module.ReconciliationDetailsPage })));
const ReconciliationActionButtons = lazy(() => import("./components/reconciliationDetails/reconciliationDetailsPage").then(module => ({ default: module.ReconciliationActionButtons })));

export enum ReconciliationRoutes {
    AllReconciliations = "/reconciliations",
    NewReconciliation = "/reconciliations/new",
    Templates = "/templates",
    CreateTemplate = "/templates/new",
    EditTemplate = "/templates/:id/edit",
    Rulesets = "/rulesets",
    CreateRuleset = "/rulesets/new",
    EditRuleset = "/rulesets/:id/edit",
    ReconciliationDetails = "/reconciliations/:id",
    Settings = "/settings"
}

const RoutesHandleLabels = {
    AllReconciliations: {
        url: "/reconciliations",
        crumb: 'All reconciliations',
        header: "Reconciliations",
    },
    NewReconciliation: {
        url: "/reconciliations/new",
        crumb: 'New reconciliation',
    },
    Templates: {
        url: "/templates",
        crumb: 'Template management',
        header: "Template management",
    },
    CreateTemplate: {
        url: "/templates/new",
        crumb: 'New template',
    },
    EditTemplate: {
        url: "/templates/:id/edit",
        crumb: 'Edit template',
    },
    Rulesets: {
        url: "/rulesets",
        crumb: 'Ruleset management',
        header: "Ruleset management",
    },
    CreateRuleset: {
        url: "/rulesets/new",
        crumb: 'New ruleset',
    },
    EditRuleset: {
        url: "/rulesets/:id/edit",
        crumb: 'Edit ruleset',
    },
    ReconciliationDetails: {
        url: "/reconciliations/:id",
        crumb: ''
    },
    Settings: {
        url: "/settings",
        header: "Settings",
        crumb:'Settings',
    }
}

const GetTemplateById = async ({ params }: any) => {
    const { id } = params;
    let returnVal = null;
    await TemplateService2.getTemplateById(id).then((res) => {
        returnVal = res.data;
    }).catch(() => {
        returnVal = false;
    })
    return returnVal;
}

const GetRulesetById = async ({ params }: any) => {
    const { id } = params;
    let returnVal = null;
    await RuleSummaryService2.getDataForRuleset(id).then((res) => {
        returnVal = res.data;
    }).catch(() => {
        returnVal = false;
    })
    return returnVal;
}

const getReconciliationDetails = async ({ params }: any) => {
    const { id } = params;
    let returnVal = null;
    await ReconciliationDetailsService.getReconciliationById(id).then((res) => {
        if (res?.data) {
            returnVal = res.data;
            returnVal.customStatus = returnVal.status === IReconciliationStatus.ReadyToReview ? 'Ready to review' : returnVal.status
            returnVal.key = returnVal.id;
            returnVal.reconciliationPeriod = `${dateFormat(returnVal?.periodStartDateUtc)} - ${dateFormat(returnVal?.periodEndDateUtc)}`
            returnVal.hasPendingChanges = returnVal.hasPendingSourceChanges || false;
        }
    }).catch(() => {
        returnVal = false;
    })
    return returnVal;
}

const getSettings = async () => {
    try {
        const res = await settingsService.getSettings();
        return res?.data || null;
    } catch {
        return false;
    }
}
const reconciliationRouter = createHashRouter([
    {
        path: "/",
        element: (
            <LocationTracker>
                <ReconciliationHome />
            </LocationTracker>
        ),    
        children: [
            {
                path: ReconciliationRoutes.AllReconciliations,
                element: <AllReconciliations />,
                handle: {
                    crumb: () => RoutesHandleLabels.AllReconciliations.crumb,
                    header: RoutesHandleLabels.AllReconciliations.header,
                    buttons: {
                        label: "Start reconciliation",
                        key: ReconciliationRoutes.NewReconciliation,
                    }
                },
            },
            {
                path: ReconciliationRoutes.ReconciliationDetails,
                element: <ReconciliationDetailsPage />,
                loader: async ({ params }: any) => {
                    const [reconciliationDetails, settings] = await Promise.all([
                        getReconciliationDetails({ params }),
                        getSettings()
                    ]);
                    return { ...(reconciliationDetails || {}), syncEnabled: settings?.syncEnabled || false };
                },
                handle: {
                    crumb: () => '',
                    buttonComponent: <ReconciliationActionButtons />
                },
            },
            {
                path: ReconciliationRoutes.NewReconciliation,
                element: <ReconciliationForm />,
                handle: {
                    crumb: () => RoutesHandleLabels.NewReconciliation.crumb,
                },
            },
            {
                path: ReconciliationRoutes.Templates,
                element: <AllTemplates />,
                handle: {
                    crumb: () => RoutesHandleLabels.Templates.crumb,
                    header: RoutesHandleLabels.Templates.header,
                    buttons: {
                        label: "Create template",
                        key: ReconciliationRoutes.CreateTemplate,
                    }
                },
            },
            {
                path: ReconciliationRoutes.CreateTemplate,
                element: <TemplateForm />,
                handle: {
                    crumb: () => RoutesHandleLabels.CreateTemplate.crumb,
                },
            },
            {
                path: ReconciliationRoutes.EditTemplate,
                element: <TemplateForm />,
                loader: GetTemplateById,
                handle: {
                    crumb: () => RoutesHandleLabels.EditTemplate.crumb,
                },
            },
            {
                path: ReconciliationRoutes.Rulesets,
                element: <AllRules />,
                handle: {
                    crumb: () => RoutesHandleLabels.Rulesets.crumb,
                    header: RoutesHandleLabels.Rulesets.header,
                    buttons: {
                        label: "Create ruleset",
                        key: ReconciliationRoutes.CreateRuleset,
                    }
                },
            },
            {
                path: ReconciliationRoutes.CreateRuleset,
                element: <RulesetForm />,
                handle: {
                    crumb: () => RoutesHandleLabels.CreateRuleset.crumb,
                },
            },
            {
                path: ReconciliationRoutes.EditRuleset,
                element: <RulesetForm />,
                loader: GetRulesetById,
                handle: {
                    crumb: () => RoutesHandleLabels.EditRuleset.crumb,
                },
            },
            ...(Config.settings === "settingsOn"? [
                {
                    path: ReconciliationRoutes.Settings,
                    element: <Settings />,
                    handle: {
                        crumb: () => RoutesHandleLabels.Settings.crumb,
                        header: RoutesHandleLabels.Settings.header,
                    },
                    loader: getSettings,
                }
            ] : []),
            {
                path: "/",
                element: <Navigate to={ReconciliationRoutes.AllReconciliations} />
            }
        ]
    },
    {
        path: "*",
        element: <Navigate to={ReconciliationRoutes.AllReconciliations} />
    }
], {
    future: {
        v7_relativeSplatPath: true,
    }
});

export const burgermenuItems = [
    {
        label: 'ReconcilePro',
        key: '1',
        children: [
            {
                label: RoutesHandleLabels.AllReconciliations.crumb,
                key: ReconciliationRoutes.AllReconciliations
            },
            {
                label: RoutesHandleLabels.NewReconciliation.crumb,
                key: ReconciliationRoutes.NewReconciliation
            },
            {
                label: RoutesHandleLabels.Templates.crumb,
                key: ReconciliationRoutes.Templates
            },
            {
                label: RoutesHandleLabels.Rulesets.crumb,
                key: ReconciliationRoutes.Rulesets
            },
            ...(Config.settings === "settingsOn" ? [{
                label: RoutesHandleLabels.Settings.crumb,
                key: ReconciliationRoutes.Settings
            }] : [])
        ]
    },
];

export default reconciliationRouter;
